import { useEffect, useMemo, useRef } from "react";

// useDebouncedCallback — fires the latest version of `fn` `wait`ms after the
// last call. Returns a stable function reference so it can be passed to
// react-hook-form's `watch` without rebinding every render.
//
// Bucket 9 (Phase 6) uses this for the 500ms MDF draft auto-save. The
// referenced callback (`fn`) can change between renders without resetting
// the in-flight debounce — the ref-based latest-fn lookup means we always
// invoke the freshest closure, which is important when the form has
// freshly mutated state to send.

export function useDebouncedCallback<Args extends unknown[]>(
  fn: (...args: Args) => void,
  wait: number,
): {
  (...args: Args): void;
  cancel: () => void;
  flush: () => void;
} {
  const fnRef = useRef(fn);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastArgsRef = useRef<Args | null>(null);

  useEffect(() => {
    fnRef.current = fn;
  }, [fn]);

  return useMemo(() => {
    const cancel = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      lastArgsRef.current = null;
    };

    const flush = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      if (lastArgsRef.current) {
        const args = lastArgsRef.current;
        lastArgsRef.current = null;
        fnRef.current(...args);
      }
    };

    const debounced = (...args: Args) => {
      lastArgsRef.current = args;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        if (lastArgsRef.current) {
          const a = lastArgsRef.current;
          lastArgsRef.current = null;
          fnRef.current(...a);
        }
      }, wait);
    };

    return Object.assign(debounced, { cancel, flush });
  }, [wait]);
}
